import React, { useEffect, useState, useContext } from "react";
import styled from "styled-components";
import {
  collection,
  getDocs,
  query,
  orderBy,
  limit,
  where,
  addDoc,
} from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import { motion } from "framer-motion";
import { Briefcase, User, Clock, Coins, MessageSquare } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../context/UserContext";
import AuthModal from "../components/modal/AuthModal";

const HomeFreelanceProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const { user } = useContext(UserContext);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const q = query(
          collection(db, "freelanceProjects"),
          orderBy("createdAt", "desc"),
          limit(8)
        );
        const snap = await getDocs(q);
        const data = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setProjects(data);
      } catch (err) {
        console.error("Error fetching projects:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  const handleDiscuss = async (dev) => {
    try {
      if (!user || !user.id) return setShowAuthModal(true);

      const chatsRef = collection(db, "chats");
      const q = query(chatsRef, where("members", "array-contains", user.id));
      const snapshot = await getDocs(q);

      let chatId = null;
      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        if (data.members.includes(dev.id)) chatId = docSnap.id;
      });

      if (!chatId) {
        const newChat = await addDoc(collection(db, "chats"), {
          members: [user.id, dev.id],
          membersInfo: {
            [user.id]: {
              name: user.name,
              email: user.email,
              profilePic: user.profilePic || null,
            },
            [dev.id]: {
              name: dev.name || "Unnamed User",
              email: dev.email || null,
              profilePic: dev.profilePic || null,
            },
          },
          createdAt: new Date(),
          messages: [],
        });
        chatId = newChat.id;
      }
      navigate("/my-chats");
    } catch (err) {
      console.error("Error initiating chat:", err);
    }
  };
  const handleView = (p) => {
    navigate(`/freelance-project/${p.id}`);
  };

  return (
    <SectionWrapper>
      {showAuthModal && (
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
        />
      )}

      <motion.h2
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        Latest <Highlight>Freelance Projects</Highlight>
      </motion.h2>

      <Subtitle>
        Explore real freelance projects posted by companies & developers.
      </Subtitle>

      {loading ? (
        <Message>Loading projects...</Message>
      ) : projects.length === 0 ? (
        <Message>No projects available yet 🚀</Message>
      ) : (
        <>
          <ScrollContainer>
            {projects.map((p, index) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <Card>
                  <CardHeader>
                    <IconWrapper>
                      <Briefcase size={20} color="#c59fff" />
                    </IconWrapper>
                    <h3>{p.title}</h3>
                  </CardHeader>

                  <p>{p.description?.slice(0, 90)}...</p>

                  <Meta>
                    <Tag>
                      <Coins size={14} /> {p.budget || "Not mentioned"}
                    </Tag>
                    {p.duration && (
                      <Tag>
                        <Clock size={14} /> {p.duration}
                      </Tag>
                    )}
                  </Meta>

                  {p.skills?.length > 0 && (
                    <Skills>
                      {p.skills.slice(0, 3).map((s, i) => (
                        <SkillTag key={i}>{s}</SkillTag>
                      ))}
                      {p.skills.length > 3 && (
                        <SkillTag>+{p.skills.length - 3}</SkillTag>
                      )}
                    </Skills>
                  )}

                  <PostedBy>
                    <User size={14} /> {p.postedBy || "Anonymous"}
                  </PostedBy>

                  <ButtonGroup>
                    <ViewButton onClick={() => handleView(p)}>View</ViewButton>
                    <DiscussButton onClick={() => handleDiscuss(p.dev)}>
                      <MessageSquare size={14} />
                      Discuss
                    </DiscussButton>
                  </ButtonGroup>
                </Card>
              </motion.div>
            ))}
          </ScrollContainer>

          <ViewAllButton onClick={() => navigate("/freelance-projects")}>
            View All Projects →
          </ViewAllButton>
        </>
      )}
    </SectionWrapper>
  );
};

export default HomeFreelanceProjects;

/* ---------------- Styled Components ---------------- */

const SectionWrapper = styled.section`
  width: 100%;
  background: linear-gradient(180deg, #0b0617, #000);
  color: #fff;
  padding: 100px 20px;
  text-align: center;
`;

const Highlight = styled.span`
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: #d2cde9;
  margin-top: 10px;
  margin-bottom: 40px;
`;

const Message = styled.p`
  color: #c59fff;
  font-size: 1rem;
  margin-top: 20px;
`;

const ScrollContainer = styled.div`
  display: flex;
  gap: 20px;
  overflow-x: auto;
  padding: 10px 10px 30px;
  scroll-behavior: smooth;

  &::-webkit-scrollbar {
    height: 8px;
  }
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(155, 92, 255, 0.3);
  border-radius: 16px;
  padding: 20px;
  width: 320px;
  text-align: left;
  flex-shrink: 0;
  transition: 0.3s;
  backdrop-filter: blur(6px);
  cursor: pointer;

  &:hover {
    transform: translateY(-6px);
    border-color: #c59fff;
    box-shadow: 0 4px 15px rgba(155, 92, 255, 0.3);
  }

  h3 {
    font-size: 1.05rem;
    margin-bottom: 8px;
    color: #fff;
  }

  p {
    font-size: 0.9rem;
    color: #ccc;
    margin-bottom: 10px;
    min-height: 50px;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
`;

const IconWrapper = styled.div`
  background: rgba(155, 92, 255, 0.15);
  border-radius: 50%;
  width: 34px;
  height: 34px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
`;

const Tag = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  background: rgba(155, 92, 255, 0.15);
  border: 1px solid rgba(155, 92, 255, 0.3);
  border-radius: 8px;
  padding: 4px 8px;
  font-size: 0.8rem;
  color: #c59fff;
`;

const Skills = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 10px;
`;

const SkillTag = styled.span`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(155, 92, 255, 0.2);
  border-radius: 6px;
  padding: 4px 8px;
  font-size: 0.75rem;
  color: #aaa;
`;

const PostedBy = styled.div`
  font-size: 0.85rem;
  color: #999;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  margin-bottom: 12px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-start;
`;

const ViewButton = styled.button`
  background: transparent;
  border: 1px solid #9b5cff;
  color: #c59fff;
  border-radius: 8px;
  padding: 6px 14px;
  font-weight: 600;
  cursor: pointer;
  transition: 0.3s;

  &:hover {
    background: #9b5cff;
    color: white;
  }
`;

const DiscussButton = styled(ViewButton)`
  display: flex;
  align-items: center;
  gap: 6px;
  border-color: rgba(255, 255, 255, 0.2);
  color: #fff;
  &:hover {
    background: rgba(155, 92, 255, 0.2);
  }
`;

const ViewAllButton = styled.button`
  margin-top: 30px;
  background: transparent;
  border: 1px solid #9b5cff;
  color: #c59fff;
  padding: 10px 24px;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    background: #9b5cff;
    color: white;
    box-shadow: 0 0 15px rgba(155, 92, 255, 0.4);
  }
`;
